import { Box } from '@chakra-ui/react';
import { format } from '@cloudinary/url-gen/actions/delivery';
import { auto } from '@cloudinary/url-gen/qualifiers/format';
import { cloudinary } from 'lib/cloudinary';
import Matter from 'matter-js';
import { useReducedMotion } from 'motion/react';
import { useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

interface AnimationProps {
  variant: 'softgels' | 'gummies';
}

const SCREEN_WIDTH_LG = 960;

export function Animation({ variant = 'softgels' }: AnimationProps) {
  const shouldReduceMotion = useReducedMotion();
  const sceneRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<Matter.Engine | null>(null);
  const renderRef = useRef<Matter.Render | null>(null);
  const worldBodiesRef = useRef<Matter.Body[]>([]);

  const debouncedUpdateCanvasSize = useDebouncedCallback(() => {
    if (!renderRef.current || !engineRef.current) return;

    const { Composite, Bodies } = Matter;

    const width = window.innerWidth;
    const height = window.innerHeight;

    // Update render canvas size
    renderRef.current.canvas.width = width;
    renderRef.current.canvas.height = height;
    renderRef.current.options.width = width;
    renderRef.current.options.height = height;

    // Remove old world boundaries
    worldBodiesRef.current.forEach((body) => {
      Composite.remove(engineRef.current!.world, body);
    });
    worldBodiesRef.current = [];

    // Add new world boundaries
    const offset = 10;
    const options = {
      isStatic: true,
      render: {
        visible: false,
      },
    };

    const newBodies = [
      Bodies.rectangle(
        width / 2,
        height + offset,
        width + 2 * offset,
        60,
        options,
      ),
      Bodies.rectangle(
        width + offset,
        height / 2,
        60,
        height + 2 * offset,
        options,
      ),
      Bodies.rectangle(-offset, height / 2, 60, height + 2 * offset, options),
    ];

    newBodies.forEach((body) => {
      Composite.add(engineRef.current!.world, body);
      worldBodiesRef.current.push(body);
    });
  }, 250);

  useEffect(() => {
    if (shouldReduceMotion) return undefined;

    const {
      Engine,
      Render,
      Runner,
      Common,
      MouseConstraint,
      Mouse,
      Composite,
      Bodies,
      Body,
      Events,
    } = Matter;

    let handleMouseMove: ((event: MouseEvent) => void) | null = null;
    let handleResize: ((event: Event) => void) | null = null;

    const getSprites = () => {
      const softgels = [1, 2, 3, 4, 5].map((n) =>
        cloudinary
          .image(`constants/sprites/softgels-${n}`)
          .delivery(format(auto()))
          .toURL(),
      );
      const gummies = [1, 2, 3, 4, 5].map((n) =>
        cloudinary
          .image(`constants/sprites/gummies-${n}`)
          .delivery(format(auto()))
          .toURL(),
      );
      if (variant === 'softgels') {
        return softgels;
      }
      return gummies;
    };

    const sprites = getSprites();

    // Preload images
    const images = sprites.map((src) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
      return img;
    });

    let loaded = 0;
    const onLoad = () => {
      loaded += 1;
      if (loaded === sprites.length) {
        startMatter();
      }
    };
    images.forEach((img) => (img.onload = onLoad));

    let engine: Matter.Engine | null = null;
    let render: Matter.Render | null = null;
    let runner: Matter.Runner | null = null;
    let worldBodies: Matter.Body[] = [];
    let centralCircle: Matter.Body | null = null;
    let interval: NodeJS.Timeout | null = null;
    let stopAddingTimeout: NodeJS.Timeout | null = null;

    function getRandomSprite() {
      return sprites[Math.floor(Math.random() * sprites.length)];
    }

    function updateCanvasSize() {
      if (!render || !engine) return;

      const width = window.innerWidth;
      const height = window.innerHeight;

      // Update render canvas size
      render.canvas.width = width;
      render.canvas.height = height;
      render.options.width = width;
      render.options.height = height;

      // Remove and recreate central circle at new position (only if screen width >= 768px)
      if (centralCircle) {
        Composite.remove(engine!.world, centralCircle);
      }

      if (width >= SCREEN_WIDTH_LG) {
        const newCenterX = width / 2;
        const newCenterY = height / 2;
        const circleRadius = 250;

        centralCircle = Bodies.circle(newCenterX, newCenterY, circleRadius, {
          isStatic: true,
          isSensor: false,
          render: {
            visible: false,
          },
        });
        Composite.add(engine!.world, centralCircle);
      } else {
        centralCircle = null;
      }

      // Remove old world boundaries
      worldBodies.forEach((body) => {
        Composite.remove(engine!.world, body);
      });
      worldBodies = [];

      // Add new world boundaries
      const offset = 10;
      const options = {
        isStatic: true,
        render: {
          visible: false,
        },
      };

      const newBodies = [
        Bodies.rectangle(
          width / 2,
          height + offset,
          width + 2 * offset,
          60,
          options,
        ),
        Bodies.rectangle(
          width + offset,
          height / 2,
          60,
          height + 2 * offset,
          options,
        ),
        Bodies.rectangle(-offset, height / 2, 60, height + 2 * offset, options),
      ];

      newBodies.forEach((body) => {
        Composite.add(engine!.world, body);
        worldBodies.push(body);
      });
    }

    function startMatter() {
      if (!sceneRef.current) return;
      const width = window.innerWidth;
      const height = window.innerHeight;

      // create engine
      engine = Engine.create();
      engineRef.current = engine;
      const { world } = engine;

      // create renderer
      render = Render.create({
        element: sceneRef.current,
        engine,
        options: {
          width,
          height,
          showAngleIndicator: false,
          wireframes: false,
          background: 'transparent',
        },
      });
      renderRef.current = render;
      Render.run(render);

      // create runner
      runner = Runner.create();
      Runner.run(runner, engine);

      // Add central circular boundary that sprites will avoid
      const centerX = width / 2;
      const centerY = height / 2;
      const circleRadius = 250;

      // Only create central circle if screen width >= 768px
      if (width >= SCREEN_WIDTH_LG) {
        centralCircle = Bodies.circle(centerX, centerY, circleRadius, {
          isStatic: true,
          isSensor: false,
          render: {
            visible: false,
          },
        });
        Composite.add(world, centralCircle);
      }

      // add initial world boundaries
      updateCanvasSize();

      // Create sprites in a ring around the central circle
      const popupArea = 300000;
      const area = width * height;
      const effectiveArea = width >= SCREEN_WIDTH_LG ? area - popupArea : area;

      // Define how many pixels of screen area correspond to one sprite
      const areaPerSprite = 5500;
      const maxSprites = 500;

      // Calculate number of sprites based on screen area, but capped
      const calculatedSprites = Math.floor(effectiveArea / areaPerSprite) * 0.7; // Fill screen by 70%
      const numSprites = Math.min(maxSprites, calculatedSprites);

      // Stack sprites more tightly on smaller screens
      const spriteSize = SCREEN_WIDTH_LG ? 38 : 24;

      const ringRadius = circleRadius + 100;
      const allSprites: Matter.Body[] = [];

      for (let i = 0; i < numSprites; i += 1) {
        const angle = (i / numSprites) * 2 * Math.PI;
        const x = centerX + Math.cos(angle) * ringRadius;
        const y = centerY + Math.sin(angle) * ringRadius - height; // Start above screen

        const texture = getRandomSprite();
        const sprite = Bodies.circle(x, y, spriteSize, {
          density: 0.0005,
          frictionAir: 0.04,
          restitution: 0.3,
          friction: 0.01,
          render: {
            sprite: {
              texture,
              xScale: 1,
              yScale: 1,
            },
          },
        });

        allSprites.push(sprite);
        Composite.add(world, sprite);
      }

      // Add repulsion force from central circle using velocity modification
      const updateRepulsion = () => {
        // Only apply repulsion if central circle exists (screen width >= 768px)
        if (!centralCircle) return;

        allSprites.forEach((sprite) => {
          const spritePos = sprite.position;
          // Use dynamic center coordinates that update on resize
          const currentCenterX = window.innerWidth / 2;
          const currentCenterY = window.innerHeight / 2;
          const distance = Math.sqrt(
            (spritePos.x - currentCenterX) ** 2 +
              (spritePos.y - currentCenterY) ** 2,
          );

          if (distance < circleRadius + 50) {
            const repulsionAngle = Math.atan2(
              spritePos.y - currentCenterY,
              spritePos.x - currentCenterX,
            );
            const repulsionSpeed = 0.5;
            const velocityX = Math.cos(repulsionAngle) * repulsionSpeed;
            const velocityY = Math.sin(repulsionAngle) * repulsionSpeed;

            // Add to existing velocity
            Body.setVelocity(sprite, {
              x: sprite.velocity.x + velocityX,
              y: sprite.velocity.y + velocityY,
            });
          }
        });
      };

      // Add the repulsion update to the engine's update loop
      Events.on(engine, 'beforeUpdate', updateRepulsion);

      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;

      if (isTouchDevice) {
        // On touch devices, use mouse constraint for direct interaction
        const mouse = Mouse.create(render.canvas);
        const mouseConstraint = MouseConstraint.create(engine, {
          mouse,
          constraint: {
            stiffness: 0.01,
            render: {
              visible: false,
            },
          },
        });
        Composite.add(world, mouseConstraint);
        render.mouse = mouse;

        // Enable touch events on the canvas
        render.canvas.style.touchAction = 'none';
        render.canvas.addEventListener(
          'touchstart',
          (e) => {
            e.preventDefault();
          },
          { passive: false },
        );
      } else {
        // On cursor devices, create a body that follows the mouse
        const mouseBody = Bodies.circle(-100, -100, 50, {
          isStatic: true,
          render: {
            visible: false,
          },
        });
        Composite.add(world, mouseBody);

        handleMouseMove = (event: MouseEvent) => {
          Body.setPosition(mouseBody, {
            x: event.clientX,
            y: event.clientY,
          });
        };
        window.addEventListener('mousemove', handleMouseMove);
      }

      // add objects periodically
      const addFallingObjects = () => {
        const x = Common.random(100, width - 100);
        const y = -50;
        const texture = getRandomSprite();
        let sprite: Matter.Body;

        if (Common.random() > 0.5) {
          sprite = Bodies.rectangle(x, y, 64, 64, {
            render: {
              strokeStyle: '#ffffff',
              sprite: {
                texture,
                xScale: 1,
                yScale: 1,
              },
            },
          });
        } else {
          sprite = Bodies.circle(x, y, 46, {
            density: 0.0005,
            frictionAir: 0.1,
            restitution: 0.3,
            friction: 0.01,
            render: {
              sprite: {
                texture,
                xScale: 1,
                yScale: 1,
              },
            },
          });
        }
        Composite.add(world, sprite);
        allSprites.push(sprite);
      };
      interval = setInterval(addFallingObjects, 400);

      // Stop adding objects after 20 seconds
      stopAddingTimeout = setTimeout(() => {
        if (interval) {
          clearInterval(interval);
        }
      }, 20000);

      handleResize = () => {
        updateCanvasSize();
      };
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (stopAddingTimeout) clearTimeout(stopAddingTimeout);
      if (interval) clearInterval(interval);
      if (render) Matter.Render.stop(render);
      if (runner && engine) Matter.Runner.stop(runner);
      if (engine) Matter.Engine.clear(engine);

      // Clear refs
      engineRef.current = null;
      renderRef.current = null;
      worldBodiesRef.current = [];

      if (handleMouseMove) {
        window.removeEventListener('mousemove', handleMouseMove);
      }
      if (handleResize) {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [variant, debouncedUpdateCanvasSize, shouldReduceMotion]);

  // NOTE: The physics simulation can't be paused, so hide it for
  // reduced-motion users - this also keeps Chromatic snapshots stable
  if (shouldReduceMotion) return null;

  return (
    <Box
      ref={sceneRef}
      position="fixed"
      top={0}
      left={0}
      width="100vw"
      height="100vh"
      zIndex={0}
      pointerEvents="auto"
      style={{ inset: 0 }}
    />
  );
}
